import { useEffect, useMemo, useState } from 'react';
import { useNotifications } from '@goparrot-dashboard/shared-ui';
import { useI18n } from '@goparrot-dashboard/i18n';
import { useInitialRender } from '@goparrot-dashboard/shared-utils';
import { useSsoLoginCallback } from '@goparrot-dashboard-api/auth';
import { UseMutationResult } from 'react-query';
import { AuthResponseDto, SquareSSOFailedEnum } from '@goparrot/dashboard-gateway-sdk';
import { AxiosError } from 'axios';
import { SsoCallbackParams } from '@goparrot-dashboard-api/auth/src/interface';
import { useAfterLogin } from '../../../../api/auth';
import { getSsoUrlParams } from '../../../../api/auth/src/utils/getSsoUrlParams';

export type UseSsoLoginCallbackMutationReturnType = {
  ssoLoginCallback: UseMutationResult<AuthResponseDto, AxiosError, SsoCallbackParams>;
  errorMsg: string | null;
};

export const useSsoLoginCallbackMutation = (): UseSsoLoginCallbackMutationReturnType => {
  const ssoUrlParams = useMemo(() => getSsoUrlParams(), []);
  const afterLogin = useAfterLogin();
  const { getIntlString } = useI18n();
  const { isFirstRender: isInitialRender } = useInitialRender();
  const { notifyError } = useNotifications();

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const ssoLoginCallback = useSsoLoginCallback({
    onSuccess: (res) => {
      afterLogin(res);
      setErrorMsg(null);
    },
    onError: (err) => {
      if (err?.response?.status === 409 && err?.response?.data.message === SquareSSOFailedEnum.SSO_FS_USER_NOT_FOUND) {
        setErrorMsg('error.notFoundSSOUserVisible.body');
      } else {
        notifyError({
          text: getIntlString('error.wrongPassword'),
        });
      }
    },
  });

  useEffect(() => {
    if (isInitialRender && ssoUrlParams && ssoUrlParams.code && ssoUrlParams.state) {
      ssoLoginCallback.mutate(ssoUrlParams);
    }
  }, [isInitialRender, ssoLoginCallback, ssoUrlParams]);

  return { ssoLoginCallback, errorMsg };
};
